/**
 * CSV export for the console's "Download as CSV" actions (failed recipients,
 * contacts, group members, API traffic). One helper, so every export quotes,
 * escapes and protects against spreadsheet formula injection the same way.
 */

export interface CsvColumn<T> {
  /** Header text in the file. */
  label: string;
  /** Cell value for one row. */
  value: (row: T) => unknown;
}

// Spreadsheets execute cells that start with these characters as formulas.
const FORMULA_PREFIX = /^[=+\-@\t\r]/;

function cell(value: unknown): string {
  if (value === null || value === undefined) return '';
  let text = value instanceof Date ? value.toISOString() : String(value);
  // A leading apostrophe makes Excel/Sheets treat it as text. Phone numbers
  // such as +26771234567 start with "+", so they are protected too.
  if (FORMULA_PREFIX.test(text)) text = `'${text}`;
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/**
 * "'+26771234567" → "+26771234567": undoes the apostrophe `cell()` (and
 * spreadsheets) put before a leading +, =, - or @, so a downloaded CSV can be
 * uploaded again as it is.
 */
export function stripTextMarker(value: string): string {
  return /^'[=+\-@\t\r]/.test(value) ? value.slice(1) : value;
}

export function toCsv<T>(rows: readonly T[], columns: readonly CsvColumn<T>[]): string {
  const header = columns.map((column) => cell(column.label)).join(',');
  const body = rows.map((row) => columns.map((column) => cell(column.value(row))).join(','));
  return [header, ...body].join('\r\n');
}

/**
 * Save a CSV in the browser. A UTF-8 byte order mark keeps names with
 * accents readable when the file is opened in Excel. No-op during SSR.
 */
export function downloadCsv(filename: string, csv: string): void {
  if (typeof document === 'undefined') return;
  const name = filename.toLowerCase().endsWith('.csv') ? filename : `${filename}.csv`;
  const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** "failed-recipients-never-played-soka-2026-10-12" — a safe, dated file name. */
export function csvFileName(...parts: Array<string | null | undefined>): string {
  const stamp = new Date().toISOString().slice(0, 10);
  const slug = parts
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return `${slug || 'export'}-${stamp}.csv`;
}
